import React from 'preact/compat';
import { ModalWrapper, Button } from 'list/DesignSystem';
import { Sheets, ModalBody, ModalHeader, ModalFooter, Prompt } from '@ingka/modal';
import { h } from 'preact';
import { useState } from 'preact/hooks';
import { SingleItemPayload } from 'helpers/pubsub';
import trashCanPath from '@ingka/ssr-icon/paths/trash-can';
import heartPath from '@ingka/ssr-icon/paths/heart';
import { List, RetailItem } from 'adapters/list/graphqlTypes';
import Lists from 'apps/RemoveFromListModal/components/CompactLists';
import translate from 'list/utils/translate';

type Props = {
    modalOpen: boolean;
    closeModal: () => void;
    payload: SingleItemPayload;
    removeFromLists: (
        payload: SingleItemPayload,
        listNames?: string[],
        listIds?: string[],
        listItemQuantityMap?: Record<string, number>,
        keepIcon?: boolean
    ) => void;
    lists: List[];
    moveToAnotherList: () => void;
};

export const RemoveFromList = ({
    modalOpen,
    closeModal,
    payload,
    removeFromLists,
    lists,
    moveToAnotherList,
}: Props) => {
    const [selectedLists, setSelectedList] = useState<string[]>([]);
    const [selectedListsNames, setSelectedListNames] = useState<string[]>([]);
    const [showRemove, setShowRemove] = useState(false);

    const getProductName = () => {
        const item = lists[0].retail.items.find(i => i.itemNo === payload.productNumber);
        return item?.product?.globalName ?? translate('Remove product');
    };

    const onSelectionChange = (listId: string, name: string, checked: boolean) => {
        if (name === '') {
            name = translate('Default list name');
        }
        if (checked) {
            setSelectedList(lists => lists.concat(listId));
            setSelectedListNames(names => names.concat(name));
        } else {
            setSelectedList(lists => lists.filter(l => l !== listId));
            setSelectedListNames(names => names.filter(n => n !== name));
        }
    };

    const onDisplayRemove = () => {
        if (lists.length === 1) {
            const listItemQuantityMap: Record<string, number> = getListItemQuantityMap(lists, payload.productNumber);
            removeFromLists(
                payload,
                lists.map(l => l.name),
                lists.map(l => l.listId),
                listItemQuantityMap
            );
            closeModal();
        } else {
            setShowRemove(true);
        }
    };

    const onRemoveClick = () => {
        const listItemQuantityMap: Record<string, number> = getListItemQuantityMap(lists, payload.productNumber);
        removeFromLists(
            payload,
            selectedListsNames,
            selectedLists,
            listItemQuantityMap,
            selectedLists.length !== lists.length
        );
        closeModal();
    };

    const getListItemQuantityMap = (lists: List[], productNumber: string): Record<string, number> => {
        const map: Record<string, number> = {};

        for (const list of lists) {
            const quantity = list.retail.items.find((item: RetailItem) => item.itemNo === productNumber)?.quantity;
            if (quantity) map[list.listId] = quantity;
        }

        return map;
    };

    if (!showRemove) {
        return (
            <ModalWrapper visible={modalOpen} handleCloseBtn={closeModal}>
                <Prompt
                    title={getProductName()}
                    footer={
                        <ModalFooter>
                            <Button
                                key="add-to-another-list"
                                type="primary"
                                text={translate('Add to another list')}
                                onClick={moveToAnotherList}
                                ssrIcon={heartPath}
                            />
                            <Button
                                key="remove-from-list"
                                type="secondary"
                                text={translate('Remove from wish list')}
                                onClick={onDisplayRemove}
                                ssrIcon={trashCanPath}
                            />
                        </ModalFooter>
                    }
                />
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper visible={modalOpen} handleCloseBtn={closeModal}>
            <Sheets
                header={
                    <ModalHeader
                        title={translate('Remove product from', {
                            product: getProductName(),
                        })}
                    />
                }
                footer={
                    <ModalFooter>
                        <Button
                            key="create-list"
                            type="primary"
                            htmlType="submit"
                            text={translate('Remove product')}
                            disabled={selectedLists.length === 0}
                            onClick={onRemoveClick}
                            loading={false}
                            ssrIcon={trashCanPath}
                        />
                    </ModalFooter>
                }
            >
                <ModalBody>
                    <Lists lists={lists} onChange={onSelectionChange} />
                </ModalBody>
            </Sheets>
        </ModalWrapper>
    );
};

export default RemoveFromList;
